// src/components/ProfileSetupScreen.jsx
// ─────────────────────────────────────────────────────────────
//  Multi-step onboarding wizard shown once after account creation.
//  Steps:
//    1. Welcome
//    2. Role selection (who are you?)
//    3. Life stage selection (what season?)
//    4. Your info (name + context name)
//    5. Celebration / done
//
//  Every role is anchored to the same discipleship core —
//  2 Timothy 2:2 — only the vocabulary and extra features change.
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { useProfile, ROLES, LIFE_STAGES, ROLE_LIFE_STAGES } from '@/contexts/ProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import appIcon from '/icons/icon-512.png';
import { resolveInviteInput, queueInvite } from '@/lib/inviteCode';

// ── Step constants ─────────────────────────────────────────────
const STEPS = {
  WELCOME:    0,
  ROLE:       1,
  LIFE_STAGE: 2,
  YOUR_INFO:  3,
  PAUL:       4,
  DONE:       5,
};
const TOTAL_PROGRESS_STEPS = 3; // steps 1-3 show progress bar

// ── Shared styles ──────────────────────────────────────────────
const BG = {
  minHeight:  '100vh',
  background: 'linear-gradient(160deg, #14532d 0%, #1c1917 60%, #292524 100%)',
  display:    'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding:    '24px 16px 40px',
  overflowY:  'auto',
};

const CARD = {
  width:        '100%',
  maxWidth:     '480px',
  background:   'rgba(255,255,255,0.05)',
  border:       '1px solid rgba(255,255,255,0.10)',
  borderRadius: '20px',
  padding:      '28px 24px',
};

const primaryBtn = (disabled = false) => ({
  width:        '100%',
  padding:      '14px',
  borderRadius: '12px',
  border:       'none',
  background:   disabled
    ? 'rgba(255,255,255,0.1)'
    : 'linear-gradient(135deg, #4d7c0f, #166534)',
  color:        disabled ? 'rgba(255,255,255,0.3)' : 'white',
  fontSize:     '1rem',
  fontWeight:   '700',
  cursor:       disabled ? 'not-allowed' : 'pointer',
  letterSpacing:'0.02em',
  fontFamily:   'inherit',
  transition:   'opacity 0.2s',
});

const ghostBtn = {
  background:  'none',
  border:      'none',
  color:       'rgba(255,255,255,0.4)',
  fontSize:    '0.85rem',
  cursor:      'pointer',
  padding:     '8px 0',
  fontFamily:  'inherit',
  width:       '100%',
  textAlign:   'center',
};

const inputStyle = {
  width:        '100%',
  padding:      '12px 14px',
  borderRadius: '10px',
  border:       '1px solid rgba(255,255,255,0.12)',
  background:   'rgba(255,255,255,0.07)',
  color:        'white',
  fontSize:     '0.95rem',
  outline:      'none',
  boxSizing:    'border-box',
  fontFamily:   'inherit',
  marginBottom: '12px',
};

// ── Progress bar ───────────────────────────────────────────────
function ProgressBar({ step }) {
  const pct = Math.round((step / TOTAL_PROGRESS_STEPS) * 100);
  return (
    <div style={{ width: '100%', maxWidth: '480px', marginBottom: '20px' }}>
      <div style={{
        display:       'flex',
        justifyContent:'space-between',
        marginBottom:  '6px',
      }}>
        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
          Step {step} of {TOTAL_PROGRESS_STEPS}
        </span>
        <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
          {pct}%
        </span>
      </div>
      <div style={{
        height:       '4px',
        background:   'rgba(255,255,255,0.1)',
        borderRadius: '99px',
        overflow:     'hidden',
      }}>
        <div style={{
          height:     '4px',
          width:      `${pct}%`,
          background: 'linear-gradient(90deg, #4d7c0f, #86efac)',
          borderRadius:'99px',
          transition: 'width 0.4s ease',
        }} />
      </div>
    </div>
  );
}

// ── Role card ─────────────────────────────────────────────────
function RoleCard({ role, selected, onClick }) {
  return (
    <button
      onClick={() => onClick(role.id)}
      style={{
        background:   selected ? 'rgba(74, 124, 15, 0.25)' : 'rgba(255,255,255,0.04)',
        border:       `2px solid ${selected ? '#86efac' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '14px',
        padding:      '14px 12px',
        cursor:       'pointer',
        textAlign:    'left',
        transition:   'all 0.2s',
        display:      'flex',
        flexDirection:'column',
        gap:          '4px',
      }}
    >
      <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>{role.emoji}</span>
      <span style={{
        color:       'white',
        fontWeight:  '700',
        fontSize:    '0.9rem',
        lineHeight:  '1.2',
        fontFamily:  'inherit',
      }}>
        {role.label}
      </span>
      <span style={{
        color:      'rgba(255,255,255,0.45)',
        fontSize:   '0.72rem',
        lineHeight: '1.3',
        fontFamily: 'inherit',
      }}>
        {role.description}
      </span>
    </button>
  );
}

// ── Life stage card ───────────────────────────────────────────
function StageCard({ stage, selected, onClick }) {
  return (
    <button
      onClick={() => onClick(stage.id)}
      style={{
        background:   selected ? 'rgba(74, 124, 15, 0.25)' : 'rgba(255,255,255,0.04)',
        border:       `2px solid ${selected ? '#86efac' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '12px',
        padding:      '12px',
        cursor:       'pointer',
        textAlign:    'center',
        transition:   'all 0.2s',
        display:      'flex',
        flexDirection:'column',
        alignItems:   'center',
        gap:          '4px',
      }}
    >
      <span style={{ fontSize: '1.4rem' }}>{stage.emoji}</span>
      <span style={{
        color:      'white',
        fontWeight: '600',
        fontSize:   '0.82rem',
        lineHeight: '1.2',
        fontFamily: 'inherit',
      }}>
        {stage.label}
      </span>
      {stage.ageRange && (
        <span style={{
          color:      'rgba(255,255,255,0.4)',
          fontSize:   '0.68rem',
          fontFamily: 'inherit',
        }}>
          {stage.ageRange}
        </span>
      )}
    </button>
  );
}

// ── Step 0: Welcome ────────────────────────────────────────────
function WelcomeStep({ onNext }) {
  return (
    <div style={{ ...CARD, textAlign: 'center', gap: '0' }}>
      <h1 style={{
        color:        'white',
        fontFamily:   'Georgia, serif',
        fontWeight:   '800',
        fontSize:     '1.5rem',
        margin:       '0 0 8px',
        lineHeight:   '1.2',
      }}>
        Welcome to Sheepheard
      </h1>
      <p style={{
        color:        'rgba(255,255,255,0.55)',
        fontSize:     '0.85rem',
        margin:       '0 0 20px',
        lineHeight:   '1.6',
      }}>
        Every follower of Jesus is called to make disciples.
        This app is your daily companion for that mission —
        whatever your role, your season, your context.
      </p>

      <div style={{
        background:   'rgba(134,239,172,0.08)',
        border:       '1px solid rgba(134,239,172,0.2)',
        borderRadius: '12px',
        padding:      '14px',
        marginBottom: '20px',
        textAlign:    'left',
      }}>
        <p style={{
          color:      '#86efac',
          fontWeight: '700',
          fontSize:   '0.8rem',
          margin:     '0 0 6px',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          The Core Mission
        </p>
        <p style={{
          color:      'rgba(255,255,255,0.7)',
          fontSize:   '0.85rem',
          margin:     0,
          lineHeight: '1.5',
          fontStyle:  'italic',
        }}>
          "The things you have heard from me… entrust to faithful men who
          will be able to teach others also." — 2 Timothy 2:2
        </p>
      </div>

      <p style={{
        color:      'rgba(255,255,255,0.45)',
        fontSize:   '0.8rem',
        margin:     '0 0 20px',
      }}>
        Let's take 60 seconds to personalize your experience.
      </p>

      <button onClick={onNext} style={primaryBtn()}>
        Get Started →
      </button>
    </div>
  );
}

// ── Step 1: Role ───────────────────────────────────────────────
function RoleStep({ selectedRole, onSelect, onNext, onBack }) {
  return (
    <div style={{ ...CARD }}>
      <button onClick={onBack} style={{ ...ghostBtn, textAlign: 'left', width: 'auto', marginBottom: '4px' }}>
        ← Back
      </button>
      <h2 style={{
        color:      'white',
        fontWeight: '700',
        fontSize:   '1.15rem',
        margin:     '0 0 4px',
        fontFamily: 'inherit',
      }}>
        What best describes you?
      </h2>
      <p style={{
        color:      'rgba(255,255,255,0.45)',
        fontSize:   '0.82rem',
        margin:     '0 0 18px',
        lineHeight: '1.5',
      }}>
        Choose your primary calling. You can change this later in Settings.
      </p>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap:                 '10px',
        marginBottom:        '20px',
        maxHeight:           '360px',
        overflowY:           'auto',
      }}>
        {ROLES.map(role => (
          <RoleCard
            key={role.id}
            role={role}
            selected={selectedRole === role.id}
            onClick={onSelect}
          />
        ))}
      </div>

      {/* Discipleship vocabulary preview */}
      {selectedRole && (() => {
        const role = ROLES.find(r => r.id === selectedRole);
        return role ? (
          <div style={{
            background:   'rgba(255,255,255,0.04)',
            borderRadius: '10px',
            padding:      '12px',
            marginBottom: '16px',
            fontSize:     '0.78rem',
            color:        'rgba(255,255,255,0.55)',
            lineHeight:   '1.6',
          }}>
            <span style={{ color: '#86efac', fontWeight: '700' }}>Your discipleship tree: </span>
            You → <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{role.gen1Plural}</strong>
            {' '}→ <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{role.gen2Plural}</strong>
            {' '}→ <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{role.gen3Plural}</strong>
          </div>
        ) : null;
      })()}

      <button
        onClick={onNext}
        disabled={!selectedRole}
        style={primaryBtn(!selectedRole)}
      >
        Continue →
      </button>
    </div>
  );
}

// ── Step 2: Life Stage ─────────────────────────────────────────
function LifeStageStep({ selectedRole, selectedStage, onSelect, onNext, onBack }) {
  const validStageIds = ROLE_LIFE_STAGES[selectedRole] ?? LIFE_STAGES.map(s => s.id);
  const availableStages = LIFE_STAGES.filter(s => validStageIds.includes(s.id));

  return (
    <div style={{ ...CARD }}>
      <button onClick={onBack} style={{ ...ghostBtn, textAlign: 'left', width: 'auto', marginBottom: '4px' }}>
        ← Back
      </button>
      <h2 style={{
        color:      'white',
        fontWeight: '700',
        fontSize:   '1.15rem',
        margin:     '0 0 4px',
        fontFamily: 'inherit',
      }}>
        What season of life are you in?
      </h2>
      <p style={{
        color:      'rgba(255,255,255,0.45)',
        fontSize:   '0.82rem',
        margin:     '0 0 18px',
        lineHeight: '1.5',
      }}>
        Your devotional content and insights will be tailored to your season.
      </p>

      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap:                 '10px',
        marginBottom:        '20px',
      }}>
        {availableStages.map(stage => (
          <StageCard
            key={stage.id}
            stage={stage}
            selected={selectedStage === stage.id}
            onClick={onSelect}
          />
        ))}
      </div>

      <button
        onClick={onNext}
        disabled={!selectedStage}
        style={primaryBtn(!selectedStage)}
      >
        Continue →
      </button>
    </div>
  );
}

// ── Step 3: Your Info ──────────────────────────────────────────
function YourInfoStep({ selectedRole, displayName, setDisplayName, ministryName, setMinistryName, location, setLocation, onNext, onBack }) {
  const role = ROLES.find(r => r.id === selectedRole) ?? ROLES[0];

  return (
    <div style={{ ...CARD }}>
      <button onClick={onBack} style={{ ...ghostBtn, textAlign: 'left', width: 'auto', marginBottom: '4px' }}>
        ← Back
      </button>
      <h2 style={{
        color:      'white',
        fontWeight: '700',
        fontSize:   '1.15rem',
        margin:     '0 0 4px',
        fontFamily: 'inherit',
      }}>
        A little about you
      </h2>
      <p style={{
        color:      'rgba(255,255,255,0.45)',
        fontSize:   '0.82rem',
        margin:     '0 0 18px',
        lineHeight: '1.5',
      }}>
        Everything below is optional and stored privately on your device.
      </p>

      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
        Your name
      </label>
      <input
        style={inputStyle}
        type="text"
        placeholder="e.g. Ptr. Rey"
        value={displayName}
        onChange={e => setDisplayName(e.target.value)}
        autoFocus
      />

      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
        {role.contextPrompt}
      </label>
      <input
        style={inputStyle}
        type="text"
        placeholder={role.contextDefault}
        value={ministryName}
        onChange={e => setMinistryName(e.target.value)}
      />

      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', display: 'block', marginBottom: '6px', fontWeight: '600' }}>
        City / Location <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: '400' }}>(optional)</span>
      </label>
      <input
        style={{ ...inputStyle, marginBottom: '20px' }}
        type="text"
        placeholder="e.g. Iloilo City"
        value={location}
        onChange={e => setLocation(e.target.value)}
      />

      <button onClick={onNext} style={primaryBtn()}>
        Let's Go! →
      </button>
    </div>
  );
}

// ── Step 4: Done ───────────────────────────────────────────────
function PaulStep({ onNext, onBack }) {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;
  const [entry,      setEntry]      = useState('');
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState('');
  const [linkedName, setLinkedName] = useState('');
  // Arrived through an invite link — the code is already stored and will be completed after setup.
  const [hasInvite] = useState(() => {
    try { return !!localStorage.getItem('sheepheard:pendingInvite'); } catch { return false; }
  });
  const connected = hasInvite || !!linkedName;

  const handleLink = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const found = await resolveInviteInput(entry, uid);
      queueInvite(found.code);
      setLinkedName(found.name);
    } catch (err) {
      setError(err?.message || 'Could not check that code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ ...CARD }}>
      <button onClick={onBack} style={{ ...ghostBtn, textAlign: 'left', width: 'auto', marginBottom: '4px' }}>
        ← Back
      </button>
      <h2 style={{ color: 'white', fontWeight: '700', fontSize: '1.15rem', margin: '0 0 4px', fontFamily: 'inherit' }}>
        Do you have a Paul?
      </h2>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', margin: '0 0 18px', lineHeight: '1.5' }}>
        Your Paul is the person discipling you. Paste the invite link or code they sent. No Paul yet? Skip this, and you can connect later from your Dashboard.
      </p>

      {connected ? (
        <div style={{
          background: 'rgba(134,239,172,0.08)', border: '1px solid rgba(134,239,172,0.2)',
          borderRadius: '12px', padding: '14px', marginBottom: '16px',
          color: '#86efac', fontSize: '0.85rem', lineHeight: '1.5',
        }}>
          {linkedName
            ? `✓ You'll be connected to ${linkedName} as your Paul when setup finishes.`
            : '✓ Your invite link will connect you to your Paul when setup finishes.'}
        </div>
      ) : (
        <>
          <input
            style={inputStyle}
            type="text"
            placeholder="Paste invite link or code"
            value={entry}
            onChange={e => { setEntry(e.target.value); setError(''); }}
            autoComplete="off"
          />
          {error && <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: '-4px 0 12px' }} role="alert">{error}</p>}
          <button
            onClick={handleLink}
            disabled={busy || !entry.trim()}
            style={{ ...primaryBtn(busy || !entry.trim()), marginBottom: '12px' }}
          >
            {busy ? 'Checking…' : 'Link my Paul'}
          </button>
        </>
      )}

      <button onClick={onNext} style={connected ? primaryBtn() : ghostBtn}>
        {connected ? 'Continue →' : 'Skip for now →'}
      </button>
    </div>
  );
}

function DoneStep({ profile, onFinish }) {
  const role      = ROLES.find(r => r.id === profile.role) ?? ROLES[0];
  const lifeStage = LIFE_STAGES.find(s => s.id === profile.lifeStage) ?? LIFE_STAGES[1];

  const milestones = [
    { emoji: '📖', text: 'Daily devotional tailored to you' },
    { emoji: '🌳', text: `Discipleship tree with your ${role.gen1Plural}` },
    { emoji: '🎯', text: 'Discipleship challenges & milestones' },
    { emoji: '🙏', text: 'Prayer journal & Bible reading plan' },
    { emoji: '👥', text: `${role.peopleLabel} Keeper` },
  ];

  return (
    <div style={{ ...CARD, textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🎉</div>

      <h2 style={{
        color:      'white',
        fontFamily: 'Georgia, serif',
        fontWeight: '800',
        fontSize:   '1.3rem',
        margin:     '0 0 6px',
      }}>
        You're all set{profile.displayName ? `, ${profile.displayName}` : ''}!
      </h2>

      <p style={{
        color:      'rgba(255,255,255,0.55)',
        fontSize:   '0.82rem',
        margin:     '0 0 20px',
        lineHeight: '1.5',
      }}>
        Your discipleship journey starts today.
      </p>

      {/* Profile summary */}
      <div style={{
        background:   'rgba(255,255,255,0.05)',
        borderRadius: '12px',
        padding:      '14px',
        marginBottom: '20px',
        textAlign:    'left',
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '1.8rem' }}>{role.emoji}</span>
          <div>
            <p style={{ color: 'white', fontWeight: '700', margin: '0', fontSize: '0.9rem' }}>
              {role.label}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', margin: '0', fontSize: '0.75rem' }}>
              {lifeStage.emoji} {lifeStage.label}
              {profile.ministryName ? ` · ${profile.ministryName}` : ''}
            </p>
          </div>
        </div>
        <div style={{
          borderTop:   '1px solid rgba(255,255,255,0.08)',
          paddingTop:  '10px',
          fontSize:    '0.75rem',
          color:       'rgba(255,255,255,0.45)',
        }}>
          <strong style={{ color: '#86efac' }}>Your discipleship tree: </strong>
          You → {role.gen1Plural} → {role.gen2Plural} → {role.gen3Plural}
        </div>
      </div>

      {/* What's waiting */}
      <div style={{
        textAlign:    'left',
        marginBottom: '20px',
      }}>
        <p style={{
          color:       'rgba(255,255,255,0.45)',
          fontSize:    '0.72rem',
          letterSpacing:'0.08em',
          textTransform:'uppercase',
          fontWeight:  '700',
          marginBottom:'10px',
        }}>
          What's waiting for you
        </p>
        {milestones.map((m, i) => (
          <div key={i} style={{
            display:      'flex',
            alignItems:   'center',
            gap:          '10px',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '1rem' }}>{m.emoji}</span>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>
              {m.text}
            </span>
          </div>
        ))}
      </div>

      <button onClick={onFinish} style={primaryBtn()}>
        Start My Discipleship Journey
      </button>
    </div>
  );
}

// ── Main ProfileSetupScreen ────────────────────────────────────
export default function ProfileSetupScreen() {
  const { completeSetup } = useProfile();
  const { user } = useAuth();

  const [step,         setStep]         = useState(STEPS.WELCOME);
  const [selectedRole, setSelectedRole] = useState('planter');
  const [selectedStage,setSelectedStage]= useState('youngAdult');
  const [displayName,  setDisplayName]  = useState(user?.displayName ?? '');
  const [ministryName, setMinistryName] = useState('');
  const [location,     setLocation]     = useState('');

  // When role changes, auto-reset life stage to first valid option
  useEffect(() => {
    const validIds = ROLE_LIFE_STAGES[selectedRole] ?? [];
    if (!validIds.includes(selectedStage)) {
      setSelectedStage(validIds[0] ?? 'youngAdult');
    }
  }, [selectedRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFinish = async () => {
    const trimmedName = displayName.trim();

    // 1. Write to ProfileContext (IDB → Firestore via useSyncStore)
    completeSetup({
      role:         selectedRole,
      lifeStage:    selectedStage,
      displayName:  trimmedName,
      ministryName: ministryName.trim(),
      location:     location.trim(),
    });

    // 2. Sync to Firebase Auth so the Layout avatar (useAvatarProfile reads
    //    user.displayName via onAuthStateChanged) shows the correct name
    //    immediately on first entry — without requiring a Settings save first.
    //    Registration doesn't collect a name, so user.displayName is empty here.
    if (user && trimmedName && trimmedName !== user.displayName) {
      try {
        const { updateProfile: fbUpdateProfile } = await import('firebase/auth');
        await fbUpdateProfile(user, { displayName: trimmedName });
        console.log('[ProfileSetupScreen] Firebase Auth displayName synced');
      } catch (err) {
        // Non-fatal — ProfileContext is saved; avatar syncs on next login.
        console.warn('[ProfileSetupScreen] Firebase Auth displayName sync failed:', err.message);
      }
    }
  };

  // Progress bar step (0 on welcome/done, 1-3 on content steps)
  const progressStep = (step >= STEPS.ROLE && step <= STEPS.YOUR_INFO)
    ? step
    : 0;

  return (
    <div style={BG}>
      {/* App logo */}
      <div style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        marginBottom:   '20px',
        marginTop:      '8px',
      }}>
        <img
          src={appIcon}
          alt="Sheepheard"
          style={{
            width:        '52px',
            height:       '52px',
            borderRadius: '14px',
            objectFit:    'contain',
            marginBottom: '8px',
            boxShadow:    '0 6px 20px rgba(77,124,15,0.35)',
          }}
        />
        <p style={{
          fontFamily:  'Georgia, serif',
          fontWeight:  '800',
          fontSize:    '1rem',
          color:       'white',
          margin:      0,
          letterSpacing:'-0.01em',
        }}>
          Sheepheard
        </p>
      </div>

      {/* Progress bar (steps 1–3 only) */}
      {progressStep > 0 && (
        <ProgressBar step={progressStep} />
      )}

      {/* Steps */}
      {step === STEPS.WELCOME && (
        <WelcomeStep onNext={() => setStep(STEPS.ROLE)} />
      )}

      {step === STEPS.ROLE && (
        <RoleStep
          selectedRole={selectedRole}
          onSelect={setSelectedRole}
          onNext={() => setStep(STEPS.LIFE_STAGE)}
          onBack={() => setStep(STEPS.WELCOME)}
        />
      )}

      {step === STEPS.LIFE_STAGE && (
        <LifeStageStep
          selectedRole={selectedRole}
          selectedStage={selectedStage}
          onSelect={setSelectedStage}
          onNext={() => setStep(STEPS.YOUR_INFO)}
          onBack={() => setStep(STEPS.ROLE)}
        />
      )}

      {step === STEPS.YOUR_INFO && (
        <YourInfoStep
          selectedRole={selectedRole}
          displayName={displayName}
          setDisplayName={setDisplayName}
          ministryName={ministryName}
          setMinistryName={setMinistryName}
          location={location}
          setLocation={setLocation}
          onNext={() => setStep(STEPS.PAUL)}
          onBack={() => setStep(STEPS.LIFE_STAGE)}
        />
      )}

      {step === STEPS.PAUL && (
        <PaulStep
          onNext={() => setStep(STEPS.DONE)}
          onBack={() => setStep(STEPS.YOUR_INFO)}
        />
      )}

      {step === STEPS.DONE && (
        <DoneStep
          profile={{
            role:         selectedRole,
            lifeStage:    selectedStage,
            displayName:  displayName.trim(),
            ministryName: ministryName.trim(),
          }}
          onFinish={handleFinish}
        />
      )}

      {/* Version note */}
      {step === STEPS.WELCOME && (
        <p style={{
          color:      'rgba(255,255,255,0.2)',
          fontSize:   '0.7rem',
          marginTop:  '20px',
          textAlign:  'center',
        }}>
          "My sheep hear my voice, and I know them, and they follow me." — John 10:27
        </p>
      )}
    </div>
  );
}